import { memo, useContext, useRef } from "react";

import { cn } from "@helpers/general";
import { getLuxeContent } from "@helpers/luxe";
import StoreContext from "@context/store";
import { useAnnouncementBarPopup } from "@context/AnnouncementBarProvider";
import { LuxeContext } from "@context/LuxeProvider";

import useAnnouncementBar from "@hooks/useAnnouncementBar";
import useResponsiveWithHydrationFix from "@hooks/useResponsiveWithHydrationFix";

import Section from "@components/announcementBar/Section";
import AnnouncementBarPopup from "@components/announcementBar/AnnouncementBarPopup";

function AnnouncementBar() {
  const {
    luxe: { isLuxeActive, hasLuxeInCart, shouldAddLuxeInNextATC },
  } = useContext(LuxeContext);
  const { popupOpen, setPopupOpen } = useAnnouncementBarPopup();

  const { displayAB } = useContext(StoreContext);
  const { backgroundColor, mobileContent, desktopContent, popup } = useAnnouncementBar();

  const announcementBarRef = useRef(null);

  const backgroundColorAB = useResponsiveWithHydrationFix(
    backgroundColor.mobile,
    backgroundColor.desktop
  );

  const isLuxe = isLuxeActive || hasLuxeInCart || shouldAddLuxeInNextATC;

  const hasPopup = popup?.title && popup?.title !== "null";
  const contentElements = (
    <>
      <div
        className="block desktop:hidden p-[10px] mx-[15px] rounded-[4px]"
        style={{ backgroundColor: backgroundColorAB }}
      >
        {mobileContent.map((content, i) => (
          <Section
            key={`section-announcement-mobile-${i}`}
            textIndex={i}
            textArray={getLuxeContent(content.text, isLuxe)}
            addShippingThreshold={content.addShippingThreshold}
            textNoThreshold={content.textNoThreshold}
            textPosition={content.textPosition?.toLowerCase()}
            enableRotatingText={content.enableRotatingText}
            timer={content.timer}
            displayLuxeProgressBar={content.displayLuxeProgressBar}
            backgroundColor={backgroundColorAB}
          />
        ))}
      </div>
      <div
        className="hidden desktop:flex px-[25px] py-[12px] mx-[1.74vw] rounded-[8px]"
        style={{ backgroundColor: backgroundColorAB }}
      >
        {desktopContent.map((content, i) => (
          <Section
            key={`section-announcement-desktop-${i}`}
            textIndex={i}
            textArray={getLuxeContent(content.text, isLuxe)}
            addShippingThreshold={content.addShippingThreshold}
            textNoThreshold={content.textNoThreshold}
            textPosition={content.textPosition?.toLowerCase()}
            enableRotatingText={content.enableRotatingText}
            timer={content.timer}
            displayLuxeProgressBar={content.displayLuxeProgressBar}
            backgroundColor={backgroundColorAB}
          />
        ))}
      </div>
    </>
  );

  if (hasPopup) {
    return (
      <>
        <AnnouncementBarPopup
          open={popupOpen}
          onClose={() => setPopupOpen(false)}
          backgroundColor={backgroundColorAB}
          popup={popup}
        />

        <button
          id="announcement-bar"
          ref={announcementBarRef}
          type="button"
          onClick={() => setPopupOpen(true)}
          className={cn(
            "sticky top-0 z-[99] border-none w-full cursor-pointer ab_container bg-white pt-[15px]",
            "desktop:pt-[25px] pb-0 overflow-y-hidden transition-all ease-out duration-300"
          )}
          style={{
            top: (!displayAB && (announcementBarRef.current?.offsetHeight || 0) * -1) || 0,
          }}
        >
          {contentElements}
        </button>
      </>
    );
  }

  return (
    <div
      id="announcement-bar"
      ref={announcementBarRef}
      className={cn(
        "sticky top-0 z-[99] border-none w-full m-0 cursor-pointer ab_container bg-white pt-[15px]",
        "desktop:pt-[25px] pb-0 overflow-y-hidden transition-all ease-out duration-300"
      )}
      style={{
        top: (!displayAB && (announcementBarRef.current?.offsetHeight || 0) * -1) || 0,
      }}
      data-testid="announcement-bar"
    >
      {contentElements}
    </div>
  );
}

export default memo(AnnouncementBar);
